import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { ethers } from "ethers";
import { z } from "zod";

// Schema for transfer parameters
export const TransferPrivateERC20Schema = z.object({
    tokenAddress: z.string().describe("ERC20 token contract address"),
    recipientAddress: z.string().describe("Recipient address"),
    amount: z.string().describe("Amount to transfer"),
    gasLimit: z.number().optional().describe("Optional gas limit"),
});

export type TransferPrivateERC20Params = z.infer<typeof TransferPrivateERC20Schema>;

// Basic ERC20 ABI for transfers
const ERC20_ABI = [
    "function transfer(address to, uint256 amount) returns (bool)",
    "function balanceOf(address account) view returns (uint256)",
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "event Transfer(address indexed from, address indexed to, uint256 value)"
];

export const transferPrivateErc20TokenAction: Action = {
    name: "TRANSFER_PRIVATE_ERC20_TOKEN",
    similes: [
        "TRANSFER_ERC20",
        "SEND_TOKEN",
        "TRANSFER_TOKEN",
        "SEND_ERC20",
        "TRANSFER_PRIVATE_TOKEN",
        "SEND_PRIVATE_TOKEN"
    ],
    description: "Transfer private ERC20 tokens to another address on the COTI blockchain.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - text: "${text}"`);
            
            // Check for transfer keywords with token/ERC20
            const isTokenTransferQuery = /\b(transfer|send).*\b(token|erc20)\b/i.test(text) ||
                                        /\b(token|erc20).*\b(transfer|send)\b/i.test(text);
            elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - isTokenTransferQuery: ${isTokenTransferQuery}`);
            
            // Check if message content has required fields
            if (message.content?.tokenAddress && message.content?.recipientAddress && message.content?.amount) {
                elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - structured params found`);
                return true;
            }

            // Check if text contains addresses and amount
            const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
            const amountMatch = text.match(/(\d+(?:\.\d+)?)/);
            
            elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - addresses found: ${addressMatches?.length || 0}`);
            elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - amount found: ${!!amountMatch}`);

            if (isTokenTransferQuery && addressMatches && addressMatches.length >= 2 && amountMatch) {
                elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - returning true`);
                return true;
            }

            elizaLogger.info(`TRANSFER_PRIVATE_ERC20_TOKEN validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`TRANSFER_PRIVATE_ERC20_TOKEN validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting TRANSFER_PRIVATE_ERC20_TOKEN action");

            let params: TransferPrivateERC20Params;

            // Try to get parameters from message content
            if (message.content?.tokenAddress && message.content?.recipientAddress && message.content?.amount) {
                params = TransferPrivateERC20Schema.parse(message.content);
            } else {
                // Extract from message text
                const text = message.content?.text || "";
                const addressMatches = text.match(/0x[a-fA-F0-9]{40}/g);
                const amountMatch = text.match(/(\d+(?:\.\d+)?)/);
                
                if (!addressMatches || addressMatches.length < 2 || !amountMatch) {
                    throw new Error("Could not extract required parameters from message");
                }
                
                // First address is typically the token contract, second is the recipient
                params = {
                    tokenAddress: addressMatches[0],
                    recipientAddress: addressMatches[1],
                    amount: amountMatch[1]
                };
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const wallet = walletProvider.getWallet();
            const fromAddress = walletProvider.getAddress();

            // Create contract instance
            const contract = new ethers.Contract(
                params.tokenAddress,
                ERC20_ABI,
                wallet
            );

            elizaLogger.info(`Transferring ${params.amount} tokens from ${fromAddress} to ${params.recipientAddress}`);

            // Get token info for formatting
            const [name, symbol, decimals] = await Promise.all([
                contract.name().catch(() => "Unknown Token"),
                contract.symbol().catch(() => "UNKNOWN"),
                contract.decimals().catch(() => 18)
            ]);

            // Check balance before transfer
            const balance = await contract.balanceOf(fromAddress);
            const amountWei = ethers.parseUnits(params.amount, decimals);
            
            if (balance < amountWei) {
                const formattedBalance = ethers.formatUnits(balance, decimals);
                throw new Error(`Insufficient token balance. Available: ${formattedBalance} ${symbol}, Required: ${params.amount} ${symbol}`);
            }

            // Transfer the tokens
            const tx = await contract.transfer(
                params.recipientAddress,
                amountWei,
                {
                    gasLimit: params.gasLimit || 200000
                }
            );

            const receipt = await tx.wait();

            const responseText = `Successfully transferred private ERC20 tokens!
Token: ${name} (${symbol})
Contract: ${params.tokenAddress}
Amount: ${params.amount} ${symbol}
From: ${fromAddress}
To: ${params.recipientAddress}
Transaction Hash: ${tx.hash}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        tokenAddress: params.tokenAddress,
                        amount: params.amount,
                        from: fromAddress,
                        to: params.recipientAddress,
                        transactionHash: tx.hash,
                        tokenName: name,
                        tokenSymbol: symbol,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    tokenAddress: params.tokenAddress,
                    amount: params.amount,
                    amountWei: amountWei.toString(),
                    from: fromAddress,
                    to: params.recipientAddress,
                    transactionHash: tx.hash,
                    tokenName: name,
                    tokenSymbol: symbol,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in TRANSFER_PRIVATE_ERC20_TOKEN action:", error);
            const errorMessage = `Failed to transfer private ERC20 tokens: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Transfer 100 tokens from contract 0x1234... to 0x5678...",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll transfer those ERC20 tokens for you.",
                    action: "TRANSFER_PRIVATE_ERC20_TOKEN",
                },
            },
        ],
    ],
};
